import { CommonModule, DOCUMENT } from '@angular/common';
import {
  Component,
  ElementRef,
  Input,
  OnDestroy,
  Renderer2,
  TemplateRef,
  ViewChild,
  ViewEncapsulation,
  inject,
} from '@angular/core';

import { OverflowMenuButtonComponent } from '../overflow-menu-button/overflow-menu-button.component';

@Component({
  selector: 'lib-action-menu',
  standalone: true,
  imports: [CommonModule, OverflowMenuButtonComponent],
  encapsulation: ViewEncapsulation.None,
  template: `
    <div class="action-menu" #menuRoot>
      <lib-overflow-menu-button
        [ariaLabel]="buttonAriaLabel"
        [disabled]="disabled"
        [isOpen]="isOpen"
        (buttonClicked)="toggleMenu()"
      />

      @if (isOpen && menuTemplate) {
        <div
          class="action-menu__panel"
          #menuPanel
          [class.action-menu__panel--start]="align === 'start'"
          [class.action-menu__panel--end]="align === 'end'"
          [class.action-menu__panel--up]="direction === 'up'"
          [style.left.px]="panelLeft"
          [style.top.px]="panelTop"
          role="menu"
        >
          <ng-container
            [ngTemplateOutlet]="menuTemplate"
            [ngTemplateOutletContext]="templateContext"
          />
        </div>
      }
    </div>
  `,
  styles: [`
    .action-menu {
      position: relative;
      display: inline-flex;
      align-items: center;
      justify-content: center;
    }

    .action-menu__panel {
      position: fixed;
      min-width: 176px;
      max-width: min(280px, 70vw);
      z-index: 1200;
      padding: 8px;
      border: 1px solid rgba(219, 226, 234, 0.95);
      border-radius: 16px;
      background: rgba(255, 255, 255, 0.98);
      backdrop-filter: blur(10px);
      box-shadow:
        0 20px 48px rgba(15, 23, 42, 0.16),
        0 4px 12px rgba(15, 23, 42, 0.08);
    }

    .action-menu__panel :is(button, a) {
      appearance: none;
      -webkit-appearance: none;
      width: 100%;
      min-height: 40px;
      display: flex;
      align-items: center;
      justify-content: flex-start;
      gap: 8px;
      border: 1px solid transparent;
      border-radius: 12px;
      background: transparent;
      color: #0f172a;
      padding: 10px 12px;
      font: inherit;
      font-size: 13px;
      font-weight: 600;
      text-align: left;
      text-decoration: none;
      cursor: pointer;
      transition:
        background-color 0.14s ease,
        border-color 0.14s ease,
        color 0.14s ease,
        transform 0.14s ease,
        box-shadow 0.14s ease;
    }

    .action-menu__panel :is(button, a):hover:not(:disabled) {
      background: linear-gradient(180deg, #f8fafc, #f1f5f9);
      border-color: #e2e8f0;
      color: #0f172a;
      transform: translateX(1px);
      box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.55);
    }

    .action-menu__panel :is(button, a):focus-visible {
      outline: 2px solid #2563eb;
      outline-offset: 2px;
    }

    .action-menu__panel :is(button, a):disabled {
      opacity: 0.46;
      cursor: default;
    }

    .action-menu__panel :is(button, a):disabled:hover {
      background: transparent;
      border-color: transparent;
      color: #0f172a;
      transform: none;
      box-shadow: none;
    }

    .action-menu__panel :is(button, a).action-menu__item--primary {
      background: linear-gradient(180deg, #eff6ff, #dbeafe);
      border-color: #bfdbfe;
      color: #1d4ed8;
      box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.4);
    }

    .action-menu__panel :is(button, a).action-menu__item--primary:hover:not(:disabled) {
      background: linear-gradient(180deg, #dbeafe, #bfdbfe);
      border-color: #93c5fd;
      color: #1d4ed8;
      box-shadow:
        inset 0 0 0 1px rgba(255, 255, 255, 0.48),
        0 6px 16px rgba(37, 99, 235, 0.12);
    }

    .action-menu__panel :is(button, a).action-menu__item--danger {
      background: linear-gradient(180deg, #fff7f7, #fee2e2);
      border-color: #fecaca;
      color: #b91c1c;
      box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.34);
    }

    .action-menu__panel :is(button, a).action-menu__item--danger:hover:not(:disabled) {
      background: linear-gradient(180deg, #fee2e2, #fecaca);
      border-color: #fca5a5;
      color: #991b1b;
      box-shadow:
        inset 0 0 0 1px rgba(255, 255, 255, 0.4),
        0 6px 16px rgba(220, 38, 38, 0.10);
    }

    .action-menu__panel :is(button, a).action-menu__item--subtle {
      color: #334155;
    }

    .action-menu__panel :is(button, a).action-menu__item--subtle:hover:not(:disabled) {
      color: #0f172a;
    }
  `],
})
export class ActionMenuComponent implements OnDestroy {
  private static openInstance: ActionMenuComponent | null = null;

  private readonly doc = inject(DOCUMENT);
  private readonly renderer = inject(Renderer2);

  @ViewChild('menuRoot') private readonly menuRoot?: ElementRef<HTMLElement>;
  @ViewChild('menuPanel') private readonly menuPanel?: ElementRef<HTMLElement>;

  @Input({ required: true }) public menuTemplate!: TemplateRef<{ close: () => void }>;
  @Input() public buttonAriaLabel = 'Open actions menu';
  @Input() public disabled = false;
  @Input() public align: 'start' | 'end' = 'end';
  @Input() public direction: 'down' | 'up' = 'down';

  protected isOpen = false;
  protected panelLeft = 0;
  protected panelTop = 0;
  private removeDocumentClickListener: (() => void) | null = null;
  private removeEscapeListener: (() => void) | null = null;
  private removeWindowScrollListener: (() => void) | null = null;
  private removeWindowResizeListener: (() => void) | null = null;
  protected readonly templateContext = {
    close: () => this.closeMenu(),
  };

  public ngOnDestroy(): void {
    if (ActionMenuComponent.openInstance === this) {
      ActionMenuComponent.openInstance = null;
    }

    this.teardownDocumentListeners();
  }

  protected toggleMenu(): void {
    if (this.disabled) {
      return;
    }

    if (this.isOpen) {
      this.closeMenu();
      return;
    }

    ActionMenuComponent.openInstance?.closeMenu();
    ActionMenuComponent.openInstance = this;
    this.isOpen = true;
    this.positionPanel();
    this.doc.defaultView?.setTimeout(() => this.positionPanel());
    this.setupDocumentListeners();
  }

  public closeMenu(): void {
    if (ActionMenuComponent.openInstance === this) {
      ActionMenuComponent.openInstance = null;
    }

    this.isOpen = false;
    this.teardownDocumentListeners();
  }

  private onDocumentClick(event: MouseEvent): void {
    if (!this.isOpen) {
      return;
    }

    const root = this.menuRoot?.nativeElement;
    const panel = this.menuPanel?.nativeElement;
    const target = event.target as Node | null;
    if (!root || !target) {
      this.closeMenu();
      return;
    }

    if (!root.contains(target) && !panel?.contains(target)) {
      this.closeMenu();
    }
  }

  private positionPanel(): void {
    const root = this.menuRoot?.nativeElement;
    const panel = this.menuPanel?.nativeElement;
    const viewport = this.doc.defaultView;
    const viewportWidth = viewport?.innerWidth ?? 0;
    const viewportHeight = viewport?.innerHeight ?? 0;

    if (!root || viewportWidth <= 0 || viewportHeight <= 0) {
      return;
    }

    const rootRect = root.getBoundingClientRect();
    const panelRect = panel?.getBoundingClientRect();
    const gap = 6;
    const padding = 8;
    const panelWidth = panelRect?.width || 176;
    const panelHeight = panelRect?.height || 1;
    const desiredLeft = this.align === 'start'
      ? rootRect.left
      : rootRect.right - panelWidth;
    const desiredTop = this.direction === 'up'
      ? rootRect.top - panelHeight - gap
      : rootRect.bottom + gap;

    this.panelLeft = Math.max(
      padding,
      Math.min(desiredLeft, viewportWidth - panelWidth - padding),
    );
    this.panelTop = Math.max(
      padding,
      Math.min(desiredTop, viewportHeight - panelHeight - padding),
    );
  }

  private onEscapePressed(): void {
    if (!this.isOpen) {
      return;
    }

    this.closeMenu();
    const activeElement = this.doc.activeElement as HTMLElement | null;
    activeElement?.blur();
  }

  private setupDocumentListeners(): void {
    if (this.removeDocumentClickListener || this.removeEscapeListener) {
      return;
    }

    this.removeDocumentClickListener = this.renderer.listen(
      this.doc,
      'click',
      (event: MouseEvent) => this.onDocumentClick(event),
    );
    this.removeEscapeListener = this.renderer.listen(
      this.doc,
      'keydown',
      (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          this.onEscapePressed();
        }
      },
    );
    const viewport = this.doc.defaultView;
    if (viewport) {
      this.removeWindowScrollListener = this.renderer.listen(
        viewport,
        'scroll',
        () => this.closeMenu(),
      );
      this.removeWindowResizeListener = this.renderer.listen(
        viewport,
        'resize',
        () => this.closeMenu(),
      );
    }
  }

  private teardownDocumentListeners(): void {
    this.removeDocumentClickListener?.();
    this.removeEscapeListener?.();
    this.removeWindowScrollListener?.();
    this.removeWindowResizeListener?.();
    this.removeDocumentClickListener = null;
    this.removeEscapeListener = null;
    this.removeWindowScrollListener = null;
    this.removeWindowResizeListener = null;
  }
}
